import type { InventoryVehicle } from '@carvana/merch-contracts';
import Head from 'next/head';
import { NextSeo } from 'next-seo';
import { useContext, useMemo } from 'react';
import { absoluteBaseUrl, blobCdnUrl } from '~/constants/environment';
import { PRECONNECT_LINKS } from '~/constants/preconnectLinks';
import { useDealershipMetaTag } from '~/context/dealership/StaticContext';
import InventoryContext from '~/context/srp/InventoryContext';
import RouteContext from '~/context/srp/RouteContext';
import SEOContext from '~/context/srp/SEOContext';
import type { VanityUrlWithPath } from '~/contracts/srp';
import useIsNewCarFilterApplied from '~/hooks/srp/selectors/useIsNewCarFilterApplied';
import { withErrorBoundary } from '~/utilities/ErrorBoundary';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';

function usePageDescription(
  hasNewCarFilter: boolean,
  vanityUrl?: VanityUrlWithPath
): string {
  const dealershipMetaTag = useDealershipMetaTag();

  return useMemo(() => {
    if (dealershipMetaTag) {
      return dealershipMetaTag.metaDescription;
    }

    const prefix = hasNewCarFilter ? 'Shop' : 'Shop used';
    const subject = vanityUrl ? vanityUrl.keyword : 'cars';
    const location = vanityUrl?.location ? `in ${vanityUrl.location}` : '';
    const postfix = `for sale on Carvana. Browse ${
      hasNewCarFilter ? '' : 'used '
    }cars online & have your next vehicle delivered to your door with as soon as next day delivery.`;

    return [prefix, subject, location, postfix].filter(Boolean).join(' ');
  }, [dealershipMetaTag, hasNewCarFilter, vanityUrl]);
}

function usePageTitle(
  hasNewCarFilter: boolean,
  requestUrl?: string,
  vanityUrl?: VanityUrlWithPath
): string {
  const dealershipMetaTag = useDealershipMetaTag();

  return useMemo(() => {
    if (dealershipMetaTag) {
      return dealershipMetaTag.title;
    }

    const rootTitle = `${
      hasNewCarFilter ? '' : 'Used '
    }Cars For Sale - Buy, Sell, Trade and Finance 100% Online | Carvana`;
    const defaultTitle = `Carvana | Buy ${
      hasNewCarFilter ? '' : 'Used '
    }Cars Online & Skip The Dealership`;

    if (requestUrl == null) {
      return rootTitle;
    }

    if (vanityUrl == null) {
      const url = new URL(requestUrl, absoluteBaseUrl);

      return /\/cars-v1$/.test(url.pathname) ? rootTitle : defaultTitle;
    }

    const location = vanityUrl.location ? `in ${vanityUrl.location}` : 'Online';

    return `${hasNewCarFilter ? '' : 'Used '}${
      vanityUrl.keyword
    } for Sale ${location} | Carvana`;
  }, [dealershipMetaTag, hasNewCarFilter, requestUrl, vanityUrl]);
}

function useSiteName() {
  const dealershipMetaTag = useDealershipMetaTag();

  return dealershipMetaTag?.siteName || 'Carvana';
}

function useThumbnail(vehicles: InventoryVehicle[]) {
  return useMemo(() => {
    const firstVehicle = vehicles.at(0);

    return firstVehicle?.imageUrl
      ? blobCdnUrl + firstVehicle.imageUrl
      : undefined;
  }, [vehicles]);
}

function HeadTags() {
  const { currentUrl } = useContext(RouteContext);
  const { vanityUrl } = useContext(SEOContext);
  const {
    inventory: {
      data: {
        inventory: { vehicles }
      }
    }
  } = useContext(InventoryContext);
  const hasNewCarFilter = useIsNewCarFilterApplied();
  const isNoIndex = ['/cars-v1/filters', '/cars-v1/st'].some(v =>
    currentUrl.includes(v)
  );
  const isNoFollow = currentUrl.includes('/cars-v1/st');
  const pageTitle = usePageTitle(hasNewCarFilter, currentUrl, vanityUrl);
  const pageDescription = usePageDescription(hasNewCarFilter, vanityUrl);
  const siteName = useSiteName();
  const thumbnail = useThumbnail(vehicles);

  const vehicleLds = vehicles.reduce<Array<{ id: number; properties: object }>>(
    (acc, vehicle) => {
      if (vehicle.price) {
        acc.push({
          id: vehicle.vehicleId,
          properties: {
            '@context': 'https://schema.org',
            '@type': 'Vehicle',
            itemCondition: hasNewCarFilter ? 'New' : 'Used',
            name: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
            modelDate: vehicle.year,
            manufacturer: vehicle.make,
            model: vehicle.model,
            color: vehicle.color,
            image: vehicle.imageUrl ? blobCdnUrl + vehicle.imageUrl : undefined,
            brand: vehicle.make,
            description: `${hasNewCarFilter ? '' : 'Used '}${vehicle.year} ${
              vehicle.make
            } ${vehicle.model} ${vehicle.trim} with ${
              vehicle.mileage
            } miles - $${formatAndDropDecimal(vehicle.price.total)}`,
            mileageFromOdometer: vehicle.mileage,
            sku: vehicle.stockNumber,
            vehicleIdentificationNumber: vehicle.vin,
            offers: {
              '@type': 'Offer',
              price: vehicle.price.total,
              priceCurrency: 'USD',
              availability: 'http://schema.org/InStock',
              priceValidUntil: 'January 1, 2030',
              url: `${absoluteBaseUrl}/vehicle/${vehicle.vehicleId}`
            }
          }
        });
      }

      return acc;
    },
    []
  );

  return (
    <>
      <NextSeo
        title={pageTitle}
        description={pageDescription}
        additionalLinkTags={PRECONNECT_LINKS}
        noindex={isNoIndex}
        nofollow={isNoFollow}
        canonical={
          isNoIndex
            ? undefined
            : vanityUrl?.path
            ? `${absoluteBaseUrl}/cars/${vanityUrl.path}`
            : `${absoluteBaseUrl}/cars`
        }
        openGraph={{
          type: 'website',
          siteName,
          title: pageTitle,
          url: currentUrl,
          description: pageDescription,
          images: thumbnail ? [{ url: thumbnail }] : undefined
        }}
        additionalMetaTags={[
          {
            property: 'og:image:type',
            content: 'image/jpeg'
          }
        ]}
      />

      <Head>
        {vehicleLds.map(vehicle => (
          <script
            key={`jsonld-vehicle-${vehicle.id}`}
            type="application/ld+json"
            data-testid="vehicle-ld"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(vehicle.properties)
            }}
          />
        ))}
      </Head>
    </>
  );
}

export default withErrorBoundary(HeadTags);
